import { createEffect, DEV } from "solid-js"
import { Box3, BufferGeometry, Color, DoubleSide, Mesh, MeshLambertMaterial, Texture, Vector3 } from "three"
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js"
import type { Nullable } from "../../../../../typescript"
import type { Engine } from "../../../../core/engine/Engine.type"
import { freeze } from "../../../../core/util/Object"
import type { QuadrantBound } from "../../../../Quadtree/createEntityQuadtreeEffect"
import { VERTICES_PER_TILE } from "../../../consts"
import generateUVs, { UVProjectionPlane } from "../../../fn/generateUVs"
import type { TileFloorGroupGeometryUserData } from "./createTerrainMesh1V3TileFloorGroup"
import createGeneric4x4TextureMergeEffect from "./Texture/createGeneric4x4TextureMergeEffect"
import { TextureSizeSettingId } from "./Texture/TextureSizeSettingId.enum"

const sandDuneTop = new Color(0xF7DC8A)
if (DEV) {
  freeze(sandDuneTop)
}
const wetSand = new Color(0xD4AC0D)
if (DEV) {
  freeze(wetSand)
}

const min = new Vector3(-0.5, -3.5, 0).multiplyScalar(VERTICES_PER_TILE)
const max = new Vector3(3.5, 0.5, 0).multiplyScalar(VERTICES_PER_TILE)
const boundingBox4x4 = new Box3(min, max)
if (DEV) {
  freeze(boundingBox4x4)
}

export default function createTerrainMesh1V3TileFloorLowMesh(
  engine: Engine,
  quadrant: QuadrantBound,
  geometryLowList: Array<BufferGeometry>,
  trackGeometryLowList: () => void,
  materialList: Array<MeshLambertMaterial>,
  trackMaterialList: () => void,
): Mesh {
  const { x: quadrantX, y: quadrantY } = quadrant
  const material = new MeshLambertMaterial({
    // vertexColors: true,
    map: new Texture(),
    normalMap: new Texture(),
    aoMap: new Texture(),
  })
  if (DEV) {
    material.side = DoubleSide
    // material.wireframe = true
  }
  const emptyGeometry = new BufferGeometry()
  const mesh = new Mesh(emptyGeometry, material)
  mesh.visible = false

  const geometryLowTranslatedList: Array<BufferGeometry> = []
  createEffect(() => {
    // console.log('trackGeometryLowList', geometryLowList.length)
    trackGeometryLowList()
    geometryLowTranslatedList.length = 0
    const prevGeometry = mesh.geometry
    if (geometryLowList.length > 0) {
      for (const geometry of geometryLowList) {
        const { userData } = geometry
        let out = userData.out as Nullable<BufferGeometry>
        const { node } = userData as TileFloorGroupGeometryUserData
        if (out) {
          geometryLowTranslatedList.push(out)
        } else {
          const geometry2 = geometry.clone()
          geometry2.translate((node.x - quadrantX) * VERTICES_PER_TILE, (node.y - quadrantY) * -VERTICES_PER_TILE, 0)
          geometryLowTranslatedList.push(geometry2)
          out = userData.out = geometry2
        }
        Object.assign(out.userData, userData)
        delete out.userData.out // remove circular reference
      }
      // console.time('mergeGeometries')
      const mergedGeometry = mergeGeometries(geometryLowTranslatedList)
      // console.timeEnd('mergeGeometries')

      mergedGeometry.computeBoundingBox()
      mergedGeometry.computeBoundingSphere()
      mergedGeometry.computeTangents?.()
      mergedGeometry.computeVertexNormals()

      // console.log('.  boundingBox', boundingBox.min.toArray(), boundingBox.max.toArray())
      // console.log('mergedGeometry', mergedGeometry.boundingBox?.min.toArray(), mergedGeometry.boundingBox?.max.toArray())

      generateUVs(mergedGeometry, UVProjectionPlane.XY, boundingBox4x4)

      mesh.geometry = mergedGeometry
      mesh.visible = true
      createGeneric4x4TextureMergeEffect(engine,
        quadrant, material, materialList, trackMaterialList,
        ['map', 'normalMap', 'aoMap'], TextureSizeSettingId.SM)
    } else {
      mesh.geometry = emptyGeometry
      mesh.visible = false
    }
    if (prevGeometry !== emptyGeometry) {
      prevGeometry.dispose()
    }
  })

  return mesh
}
